import { FunctionComponent, useCallback } from 'react'
import {
  Tag,
  Text,
  Tooltip,
  getTheme,
  toast,
  useMediaQuery,
} from '@conexasaude/conexa-hero'
import Icon from '@/components/Iconography'
import Modal from '@/components/Modal'
import { useStyles } from './styles'
import { capitalize, characterLimiter } from '@/utils/fnUtils'
import { AccessOrActivateProps, DependentsModalProps } from './types'
import DivisorLine from '@/components/DivisorLine'
import { RenderIf } from '@/components/RenderIf'
import { useNavigation } from '@/hooks/useNavigation'
import { usePatientStore } from '../../store/PatientStore/patient.store'
import { AnalyticEvent } from '../../types/en-us'
import { SEND_TO } from '@/modules/Signup/constants'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useLocation } from 'react-router-dom'
import { EVENT_NAME } from './constants'
import { getDigitsFromString } from '@/utils/getDigitsFromString'
import { sha256Hash } from '@conexasaude/analytics'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import themeServices from '@/theme/theme.services'
import service from '@/modules/Auth/auth-services'
import { showErrorToast } from '../../utils/requisitionErrors'
import { formatCpfNumber } from '@/utils/masks/cpf'
import { User } from '@/modules/Auth/classes/User'
import { capitalizeFirstLetter } from '@/utils/string'
import { setCache } from '@/utils/cache'
import { setRefreshToken, setToken } from '@/utils/token'

const DependentsModal: FunctionComponent<DependentsModalProps> = ({
  childDependents,
  adultDependents,
  activatedChildren,
  showAdultDependents = true,
  modalId,
}) => {
  const styles = useStyles()
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm})`)
  const navigate = useNavigation()
  const { closeModal } = useModalContext()
  const { logAnalyticsEvent, logoutFromFirebase } = useFirebaseContext()
  const { setPatientData, holder } = usePatientStore()
  const { pathname } = useLocation()
  const { users, choiceUser, setUserClinic, signOut } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()

  const handleAccessOrActivate = (data: AccessOrActivateProps) => {
    const normalizedCPF = getDigitsFromString(holder?.cpf)
    const hashedCPF = sha256Hash(normalizedCPF)

    if (!data?.dependent?.active) {
      setPatientData({
        ...data?.dependent,
        ...(data?.dependent?.cpf && {
          cpf: formatCpfNumber(data?.dependent?.cpf),
        }),
        dependentType: data?.dependentType,
      })
      settingUpAnalyticsEvent({
        interaction: 'activate_click',
        user_id: hashedCPF,
        cnx_user_id: hashedCPF,
      })
      closeModal()
      navigate(`/ativacao/dados-dependente/${data?.dependent.id}`)
    } else {
      settingUpAnalyticsEvent({
        interaction: 'access_click',
        user_id: hashedCPF,
        cnx_user_id: hashedCPF,
      })
      const user = users?.find(
        user => user?.patientId === data?.dependent?.idDependent,
      )

      setUser(user)
    }
  }

  const setUser = useCallback(
    async (user: User) => {
      try {
        const { token, id, flagAccessedBefore, refreshToken } = user

        const [userClinic, clinicColors, ip] = await Promise.all([
          service.getClinic(token),
          themeServices.getClinicColorsById(user?.clinic?.id),
          service.getUserIP().catch(() => {
            return ''
          }),
        ])

        const { clinic, flags } = userClinic

        if (!flagAccessedBefore) await service.validateFirstAccess(token)
        signOut(false)
        logoutFromFirebase()
        choiceUser(id, ip)
        setToken(token)
        setRefreshToken(refreshToken)
        setCache('user', id)
        setThemeColors(clinicColors)
        setUserClinic(userClinic)
        setSpecialities(clinic)

        if (flags?.PermiteGeolocalizacao) {
          navigator.geolocation.getCurrentPosition(position => {
            const {
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              latitude,
              longitude,
              speed,
            } = position.coords
            service.sendGeolocation({
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              latitude,
              longitude,
              speed,
            })
          })
        }

        navigate('/')
        toast({
          message: `Você entrou como ${capitalizeFirstLetter(user?.name)}.`,
          variant: 'information',
        })
      } catch (err) {
        showErrorToast(err)
      } finally {
        closeModal()
      }
    },
    [
      choiceUser,
      setThemeColors,
      setUserClinic,
      setSpecialities,
      navigate,
      closeModal,
    ],
  )

  const handleUpdateDependentData = (data: AccessOrActivateProps) => {
    if (data?.dependent?.active) return
    const normalizedCPF = getDigitsFromString(holder?.cpf)
    const hashedCPF = sha256Hash(normalizedCPF)
    settingUpAnalyticsEvent({
      interaction: 'update_click',
      user_id: hashedCPF,
      cnx_user_id: hashedCPF,
    })
    if (!data?.dependent?.active) {
      setPatientData({
        ...data?.dependent,
        dependentType: data?.dependentType,
      })
      navigate(`/ativacao/atualizar/dados-dependente/${data?.dependent.id}`)
      closeModal()
    }
  }

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      const routePath =
        pathname?.split('/')?.[2] || pathname?.split('/')?.[1] || ''
      const eventName = eventDefinitions?.eventName || EVENT_NAME[routePath]
      logAnalyticsEvent(eventName, {
        interaction: `${eventDefinitions?.interaction}`,
        options: {
          sendTo: SEND_TO,
        },
        ...(eventDefinitions?.cnx_user_id && {
          cnx_user_id: eventDefinitions?.cnx_user_id,
        }),
        ...(eventDefinitions?.user_id && {
          user_id: eventDefinitions?.user_id,
        }),
      })
    },
    [logAnalyticsEvent, pathname],
  )

  const handleDismiss = () => {
    closeModal()
    settingUpAnalyticsEvent({ interaction: 'close_click' })
  }

  return (
    <Modal id={modalId || 'DEPENDENTS_MODAL'} onDismiss={handleDismiss}>
      <div css={styles.modalContent}>
        <div css={styles.dependentSection}>
          <RenderIf condition={childDependents?.length > 0}>
            <Text
              css={styles.listTitle}
              color={theme.colors.neutral[300]}
              variant="paragraphLarge"
              renderAs="p"
            >
              Dependentes
              <Text
                renderAs="span"
                variant="paragraphLarge"
                color={theme.colors.brand.primary[500]}
              >
                crianças e adolescentes
              </Text>
            </Text>
          </RenderIf>
          <div css={styles.dependentList}>
            {childDependents?.map((dependent, index) => {
              const isActive = dependent?.active || activatedChildren
              return (
                <div key={dependent?.id}>
                  <div
                    css={styles.dependentListItem}
                    onClick={() =>
                      handleAccessOrActivate({
                        dependent,
                        dependentType: 'childDependent',
                      })
                    }
                  >
                    <div css={styles.leftAligned}>
                      <div css={styles.tagWrapper}>
                        <Tag
                          variant={isActive ? 'success' : 'error'}
                          size="small"
                          css={styles.tag}
                        >
                          {isActive ? 'Ativo' : 'Pendente'}
                        </Tag>
                      </div>
                      <Text
                        variant="paragraphSmall"
                        color={theme.colors.brand.primary[500]}
                      >
                        {characterLimiter(
                          capitalize(dependent?.name?.toLocaleLowerCase()),
                          isMobile ? 16 : 25,
                        )}
                      </Text>
                    </div>
                    <Text variant="linkUnderline">
                      {isActive ? 'Acessar' : 'Ativar'}
                    </Text>
                  </div>
                  <RenderIf
                    condition={
                      childDependents?.length > 1 &&
                      index !== childDependents?.length - 1
                    }
                  >
                    <div css={styles.divisorLineWrapper}>
                      <DivisorLine />
                    </div>
                  </RenderIf>
                </div>
              )
            })}
          </div>
          <RenderIf
            condition={adultDependents?.length > 0 && showAdultDependents}
          >
            <RenderIf condition={childDependents?.length > 0}>
              <DivisorLine />
            </RenderIf>
            <div css={styles.listTitle}>
              <Text
                renderAs="span"
                variant="paragraphLarge"
                color={theme.colors.brand.primary[500]}
              >
                Dependentes
              </Text>
              <Tooltip label="Dependentes adultos precisam criar uma conta separada.">
                <span>
                  <Icon
                    name="info"
                    size="small"
                    color={theme?.colors.neutral[200]}
                  />
                </span>
              </Tooltip>
            </div>
            <div css={styles.dependentList}>
              {adultDependents?.map((dependent, index) => {
                return (
                  <div key={dependent?.id}>
                    <div
                      css={styles.dependentListItem}
                      onClick={() =>
                        handleUpdateDependentData({
                          dependent,
                          dependentType: 'adultDependent',
                        })
                      }
                    >
                      <div css={styles.leftAligned}>
                        <div css={styles.tagWrapper}>
                          <Tag
                            variant={dependent?.active ? 'success' : 'error'}
                            size="small"
                          >
                            {dependent?.active ? 'Ativo' : 'Pendente'}
                          </Tag>
                        </div>
                        <Text
                          variant="paragraphSmall"
                          color={theme.colors.brand.primary[500]}
                        >
                          {characterLimiter(
                            capitalize(dependent?.name?.toLocaleLowerCase()),
                            isMobile ? 16 : 25,
                          )}
                        </Text>
                      </div>
                      <RenderIf condition={!dependent?.active}>
                        <Text variant="linkUnderline">Atualizar</Text>
                      </RenderIf>
                    </div>
                    <RenderIf
                      condition={
                        adultDependents?.length > 1 &&
                        index !== adultDependents?.length - 1
                      }
                    >
                      <div css={styles.divisorLineWrapper}>
                        <DivisorLine />
                      </div>
                    </RenderIf>
                  </div>
                )
              })}
            </div>
          </RenderIf>
        </div>
      </div>
    </Modal>
  )
}

export { DependentsModal }
